import { isUndefined } from 'lodash-es'
import { type FunctionComponent } from 'react'
import { useUpdateCustomizedFieldValueMutation } from '@/api/workspace-api/directory-api'
import SingleInputFieldModal from '@/components/common/modals/single-input-field-modal'
import useKolInfo from '@/components/kol/hooks/use-kol-info'
import textMaximumLength from '@/constants/text-maximum-length'
import { useIntl } from '@/i18n/hooks/use-intl'
import type { CustomizedField } from '@/types/schema/directory/kol-management'
import { ampli, ConfigureCollaborationNotesProperties } from '@/utils/ampli'

interface EditCustomizedFieldValueModalProps {
  open: boolean
  onClose: VoidFunction
  kolId: string
  field?: CustomizedField
  from?: ConfigureCollaborationNotesProperties['from']
}

const EditCustomizedFieldValueModal: FunctionComponent<
  EditCustomizedFieldValueModalProps
> = ({ open, kolId, onClose, field, from }) => {
  const { formatMessage } = useIntl()
  const { kolInfo } = useKolInfo(kolId)

  const [updateCustomizedFieldValue] = useUpdateCustomizedFieldValueMutation()

  return (
    <SingleInputFieldModal
      fieldLabel={formatMessage({
        id: 'directory:drawer_custom_field_value',
      })}
      inputProps={{
        count: {
          show: true,
          max: textMaximumLength.content,
        },
      }}
      open={open}
      required={false}
      rules={[
        {
          max: textMaximumLength.content,
          message: formatMessage(
            { id: 'error:field_max_length' },
            {
              inputField: field?.field_name,
              length: textMaximumLength.content,
            },
          ),
        },
      ]}
      title={formatMessage(
        {
          id: 'directory:drawer_custom_edit_field_name_title',
        },
        { fieldName: field?.field_name },
      )}
      value={field?.field_value ?? undefined}
      onClose={onClose}
      onSave={async (value: string): Promise<void> => {
        if (isUndefined(field)) {
          return
        }

        await updateCustomizedFieldValue({
          kolId,
          payload: { ...field, field_value: value },
        }).unwrap()

        ampli.editCustomFieldValue({
          kolId,
          kolName: kolInfo?.name,
          from,
          fieldId: field.id,
          fieldValue: value,
          isClickedFromDrawer: true,
          fieldName: field.field_name,
        })
      }}
    />
  )
}

export default EditCustomizedFieldValueModal
